import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { productCenterContent } from '@/mocks/products';
import ProductIntroDetail from './ProductIntroDetail';
import ProductShieldDetail from './ProductShieldDetail';
import ProductFundDetail from './ProductFundDetail';
import ProductServerDetail from './ProductServerDetail';
import ModelK45Detail from './ModelK45Detail';
import ModelK24Detail from './ModelK24Detail';
import ModelMK45Detail from './ModelMK45Detail';
import ModelMK24Detail from './ModelMK24Detail';
import ModelP40Detail from './ModelP40Detail';
import ModelYZ40Detail from './ModelYZ40Detail';
import ModelCXDetail from './ModelCXDetail';

export default function ProductDetail() {
  const { slug, model } = useParams<{ slug: string; model: string }>();

  // 二级路由：产品型号详情页
  if (slug === 'cloudusb-server' && model === 'cloudusb-k45') {
    return <ModelK45Detail />;
  }

  if (slug === 'cloudusb-server' && model === 'cloudusb-k24') {
    return <ModelK24Detail />;
  }

  if (slug === 'cloudusb-server' && model === 'cloudusb-mk45') {
    return <ModelMK45Detail />;
  }

  if (slug === 'cloudusb-server' && model === 'cloudusb-mk24') {
    return <ModelMK24Detail />;
  }

  if (slug === 'cloudusb-server' && model === 'cloudusb-p40') {
    return <ModelP40Detail />;
  }

  if (slug === 'cloudusb-server' && model === 'cloudusb-yz40') {
    return <ModelYZ40Detail />;
  }

  if (slug === 'cloudusb-server' && model === 'cloudusb-cx') {
    return <ModelCXDetail />;
  }

  if (slug === 'intro') {
    return <ProductIntroDetail />;
  }

  if (slug === 'shield-platform') {
    return <ProductShieldDetail />;
  }

  if (slug === 'fund-platform') {
    return <ProductFundDetail />;
  }

  if (slug === 'cloudusb-server') {
    return <ProductServerDetail />;
  }

  const product = productCenterContent.items.find((item) => item.slug === slug);

  if (!product) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="flex flex-col items-center justify-center py-40 px-4 text-center">
          <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-6">
            <i className="ri-inbox-line text-3xl" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground-50 font-heading">
            未找到该产品
          </h1>
          <p className="mt-3 text-sm text-foreground-300">
            您访问的产品不存在或已下架，请返回首页查看其它内容。
          </p>
          <Link
            to="/"
            className="mt-8 inline-flex items-center gap-2 px-8 py-3 bg-primary-500 text-foreground-50 text-sm font-semibold rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            <i className="ri-arrow-left-line" />
            返回首页
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const otherProducts = productCenterContent.items.filter(
    (item) => item.slug !== product.slug,
  );

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        {/* Header */}
        <section className="pt-28 md:pt-36 pb-10 md:pb-14 bg-background-50">
          <div className="w-full px-4 md:px-8 lg:px-12 xl:px-20 max-w-[1400px] mx-auto">
            <nav className="mb-8 flex items-center gap-2 text-sm text-foreground-300">
              <Link to="/" className="hover:text-primary-400 transition-colors">
                首页
              </Link>
              <i className="ri-arrow-right-s-line" />
              <Link
                to="/product-center"
                className="hover:text-primary-400 transition-colors"
              >
                产品中心
              </Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-foreground-100 font-medium">{product.titleCn}</span>
            </nav>

            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
              <div className="max-w-3xl">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-500 text-foreground-50">
                    <i className={`${product.icon} text-2xl`} />
                  </div>
                  <span className="text-sm font-medium text-primary-400 tracking-wide font-label">
                    {product.titleEn}
                  </span>
                </div>
                <h1 className="text-3xl md:text-5xl font-black text-foreground-50 font-heading">
                  {product.titleCn}
                </h1>
                <p className="mt-5 text-base md:text-lg text-foreground-300 leading-relaxed max-w-2xl">
                  {product.desc}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Intro: two-column */}
        <section className="pb-16 md:pb-24 bg-background-50">
          <div className="w-full px-4 md:px-8 lg:px-12 xl:px-20 max-w-[1400px] mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <h2 className="text-xl md:text-2xl font-bold text-foreground-50 font-heading">
                    产品介绍
                  </h2>
                  <div className="flex-1 h-px bg-background-200/60" />
                </div>
                <p className="text-sm md:text-base text-foreground-300 leading-loose">
                  {product.intro}
                </p>
              </div>
              <div className="relative">
                <div className="rounded-lg overflow-hidden border border-background-200/50">
                  <img
                    src={product.image}
                    alt={product.titleCn}
                    title={`${product.titleCn} ${product.titleEn}`}
                    className="w-full h-64 md:h-80 object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features: numbered cards */}
        <section className="bg-background-100 py-16 md:py-24">
          <div className="w-full px-4 md:px-8 lg:px-12 xl:px-20 max-w-[1400px] mx-auto">
            <div className="flex items-end justify-between">
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-foreground-50 font-heading">
                  核心能力
                </h2>
                <div className="mt-4 w-12 h-0.5 bg-primary-500 rounded-full" />
              </div>
              <span className="hidden sm:block text-sm text-foreground-300 font-label">
                共 {product.features.length} 项
              </span>
            </div>

            <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8">
              {product.features.map((feature, idx) => (
                <div
                  key={feature.title}
                  className="group relative bg-background-50 rounded-lg border border-background-200/50 p-8 md:p-10 overflow-hidden transition-all duration-300 hover:border-primary-500/40"
                >
                  <span className="absolute top-6 right-6 text-5xl md:text-6xl font-black text-background-200 group-hover:text-primary-400/20 transition-colors font-heading leading-none">
                    {String(idx + 1).padStart(2, '0')}
                  </span>
                  <div className="relative">
                    <div className="w-14 h-14 flex items-center justify-center rounded-lg bg-background-100 text-primary-500 mb-6 group-hover:bg-primary-500 group-hover:text-foreground-50 transition-colors">
                      <i className={`${feature.icon} text-2xl`} />
                    </div>
                    <h3 className="text-lg md:text-xl font-bold text-foreground-50">
                      {feature.title}
                    </h3>
                    <p className="mt-3 text-sm text-foreground-300 leading-relaxed">
                      {feature.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Other products */}
        <section className="py-16 md:py-24 bg-background-50">
          <div className="w-full px-4 md:px-8 lg:px-12 xl:px-20 max-w-[1400px] mx-auto">
            <div className="flex items-end justify-between">
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-foreground-50 font-heading">
                  其它产品
                </h2>
                <div className="mt-4 w-12 h-0.5 bg-primary-500 rounded-full" />
              </div>
              <Link
                to="/product-center"
                className="inline-flex items-center gap-2 text-sm font-medium text-foreground-400 hover:text-primary-400 transition-colors whitespace-nowrap"
              >
                查看全部
                <i className="ri-arrow-right-line" />
              </Link>
            </div>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {otherProducts.map((item) => (
                <Link
                  key={item.slug}
                  to={`/product-center/${item.slug}`}
                  className="group block bg-background-100 rounded-lg overflow-hidden border border-background-200/50 hover:border-primary-500/40 transition-all duration-300"
                >
                  <div className="relative w-full h-44 overflow-hidden">
                    <img
                      src={item.image}
                      alt={item.titleCn}
                      title={`${item.titleCn} ${item.titleEn}`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                    <div className="absolute bottom-3 left-4 w-10 h-10 flex items-center justify-center rounded-lg bg-background-50/90 text-primary-500">
                      <i className={`${item.icon} text-lg`} />
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-base md:text-lg font-bold text-foreground-50 group-hover:text-primary-400 transition-colors">
                      {item.titleCn}
                    </h3>
                    <p className="mt-2 text-sm text-foreground-300 leading-relaxed line-clamp-2">
                      {item.desc}
                    </p>
                    <div className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-foreground-400 group-hover:text-primary-400 transition-colors">
                      <span>查看详情</span>
                      <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}